import { createAsyncThunk } from '@reduxjs/toolkit'
import { sanitizeErrorData } from './errorHandling'
import { datadogRum } from '@datadog/browser-rum'

/**
 * Creates an async thunk with integrated error handling that logs to Datadog RUM
 * but doesn't show notifications (notifications are handled by FeeManagementErrorHandler)
 *
 * @param {string} typePrefix - The Redux action type prefix (e.g., 'feeManagement/fetchOverrideSheets')
 * @param {Function} payloadCreator - The payload creator function
 * @param {Object} options - Options for createAsyncThunk and error handling
 * @param {string} options.errorSource - Source identifier for error tracking
 * @param {Function} options.customErrorContext - Optional function to create custom context from payload
 * @returns {Function} - Enhanced async thunk
 */
export const createTrackedAsyncThunk = (typePrefix, payloadCreator, options = {}) => {
  const { errorSource = typePrefix, customErrorContext, ...thunkOptions } = options

  return createAsyncThunk(
    typePrefix,
    async (arg, thunkAPI) => {
      try {
        return await payloadCreator(arg, thunkAPI)
      } catch (error) {
        // Determine what context to include with the error
        let errorPayload

        if (typeof customErrorContext === 'function') {
          // Use custom function to extract relevant context
          errorPayload = customErrorContext(arg)
        } else {
          // For regular payloads, sanitize to limit size
          errorPayload = sanitizeErrorData(arg)
        }

        // Only log the error to Datadog, don't show a notification
        // (Notifications will be handled by FeeManagementErrorHandler)
        console.error(`[${errorSource}] Error:`, error)
        datadogRum.addError(error, {
          source: errorSource,
          context: {
            action: typePrefix,
            payload: errorPayload,
          },
        })

        // Return the error to be handled by the reducer
        return thunkAPI.rejectWithValue(error.errors?._error || error.message || `Failed in ${typePrefix}`)
      }
    },
    thunkOptions,
  )
}

/**
 * Wraps an existing thunk with error tracking
 *
 * @param {Function} thunk - Original thunk function
 * @param {Object} options - Error handling options
 * @param {Function} options.customErrorContext - Optional function to extract context from arg
 * @returns {Function} - Enhanced thunk with error tracking
 */
export const withErrorTracking = (thunk, options = {}) => {
  const { customErrorContext, ...trackingOptions } = options

  const enhancedThunk = (arg) => async (dispatch) => {
    try {
      return await dispatch(thunk(arg))
    } catch (error) {
      // Extract relevant context from the argument
      const errorContext = typeof customErrorContext === 'function' ? customErrorContext(arg) : sanitizeErrorData(arg)

      // Only log to Datadog, don't show notification
      console.error(`[${trackingOptions.source || 'unknown'}] Error:`, error)
      datadogRum.addError(error, {
        ...trackingOptions,
        context: {
          ...(trackingOptions.context || {}),
          argument: errorContext,
        },
      })

      throw error
    }
  }

  return enhancedThunk
}
